// Gmail API service for sending invitation emails
// Browser-compatible development version with server-side production support

// Development email service that simulates sending emails
class EmailService {
  private initialized = false;

  async initialize() {
    if (this.initialized) return;
    this.initialized = true;
    console.log('Email service initialized');
  }

  async sendInvitationEmail(
    to: string,
    name: string,
    tempPassword: string,
    invitationToken: string,
    senderEmail: string,
    senderName: string
  ): Promise<boolean> {
    try {
      await this.initialize();

      const subject = 'Invitation: FAN EXPO Chicago Press Center';
      const loginUrl = `${window.location.origin}/login`;
      
      const htmlBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
          <!-- Fan Expo Logo Header -->
          <div style="text-align: center; margin-bottom: 30px; padding: 20px; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);">
            <img src="${window.location.origin}/fanexpo-chicago-logo.png" alt="FAN EXPO Chicago" style="max-width: 200px; height: auto;">
            <h1 style="color: #ffffff; margin: 15px 0 0 0; font-size: 24px; font-weight: bold;">
              Invitation: FAN EXPO Chicago Press Center
            </h1>
          </div>
          
          <p>Dear <strong>${name}</strong>,</p>
          
          <p style="line-height: 1.6; color: #333; margin-bottom: 20px;">
            As an accredited member of the press, we strongly encourage you to utilize the FAN EXPO Chicago Press Center. 
            This is the easiest, fastest way to indicate interest in interviews with our guests, and to see your interview 
            request status. You can also access press materials, releases, images, and even communicate with the PR Team!
          </p>
          
          <div style="background: linear-gradient(135deg, #0f3460 0%, #16213e 100%); color: white; padding: 25px; border-radius: 8px; margin: 25px 0;">
            <h3 style="color: #ffffff; margin-top: 0; font-size: 18px;">Your Login Credentials:</h3>
            <p style="margin: 10px 0;"><strong>Email:</strong> ${to}</p>
            <p style="margin: 10px 0;"><strong>Temporary Password:</strong> <code style="background-color: rgba(255,255,255,0.2); padding: 4px 8px; border-radius: 4px; color: #ffffff; font-weight: bold;">${tempPassword}</code></p>
            <p style="margin: 10px 0;"><strong>Login URL:</strong> <a href="${loginUrl}" style="color: #4fc3f7; text-decoration: underline;">${loginUrl}</a></p>
          </div>
          
          <div style="background-color: #e3f2fd; border-left: 4px solid #2196f3; padding: 15px; border-radius: 4px; margin: 20px 0;">
            <h4 style="color: #1976d2; margin-top: 0;">Important Security Information:</h4>
            <ul style="color: #1565c0; margin: 10px 0; padding-left: 20px;">
              <li>You will be prompted to change your password on first login</li>
              <li>This temporary password expires in 7 days</li>
              <li>Keep your login credentials secure</li>
            </ul>
          </div>
          
          <div style="text-align: center; margin: 30px 0;">
            <a href="${loginUrl}" 
               style="background: linear-gradient(135deg, #0f3460 0%, #16213e 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; font-size: 16px;">
              Access Press Center
            </a>
          </div>
          
          <hr style="border: none; border-top: 1px solid #ddd; margin: 40px 0 30px 0;">
          
          <p style="color: #666; font-size: 14px; margin-bottom: 20px;">
            If you have any questions, please contact ${senderName} at <a href="mailto:${senderEmail}" style="color: #0f3460;">${senderEmail}</a>.
          </p>
          
          <!-- Acacia Logo Footer -->
          <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
            <img src="${window.location.origin}/acacia-logo.jpg" alt="Acacia Consulting Group" style="max-width: 120px; height: auto; opacity: 0.8;">
            <p style="color: #999; font-size: 11px; margin: 10px 0 0 0;">
              Powered by Acacia Consulting Group
            </p>
          </div>
        </div>
      `;

      // Check if we're in production and have a server-side email function
      if (typeof window === 'undefined' || window.location.hostname !== 'localhost') {
        console.log('🚀 PRODUCTION MODE - Calling server-side Gmail function');
        
        try {
          // Call Supabase Edge Function to send real email
          const response = await fetch(
            'https://plsafercigygayzlwdqd.supabase.co/functions/v1/send-gmail-email',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                to,
                from: `${senderName} <${senderEmail}>`,
                subject,
                htmlBody,
                senderEmail: senderEmail // For domain-wide delegation
              }),
            }
          );

          const result = await response.json();
          
          if (response.ok && result.success) {
            console.log('✅ Email sent successfully via Gmail API:', result.messageId);
            alert(`✅ REAL EMAIL SENT TO: ${name}\n\n📧 EMAIL: ${to}\n🔑 TEMPORARY PASSWORD: ${tempPassword}\n🌐 LOGIN URL: ${loginUrl}\n\n📮 MESSAGE ID: ${result.messageId}`);
            return true;
          } else {
            console.error('❌ Gmail API error:', result.error);
            throw new Error(result.error || 'Gmail API failed');
          }
        } catch (error) {
          console.error('❌ Edge Function call failed:', error);
          // Fall back to development mode
          console.log('📧 Falling back to development mode');
        }
      }

      // Development mode - show email details
      console.log('📧 DEVELOPMENT MODE - Email simulation:');
      console.log({
        to,
        from: `${senderName} <${senderEmail}>`,
        subject,
        username: to,
        tempPassword,
        loginUrl,
        invitationToken,
        message: `Hi ${name}, you've been invited to the FAN EXPO Chicago Press Center by ${senderName}. Login with your email and temporary password: ${tempPassword}`
      });
      
      // Show an alert with the credentials for testing
      alert(`✅ INVITATION SENT TO: ${name}\n\n📧 EMAIL: ${to}\n🔑 TEMPORARY PASSWORD: ${tempPassword}\n🌐 LOGIN URL: ${loginUrl}\n\n⚠️ COPY THIS PASSWORD NOW!\n\n(In production, this would be sent via email)`);
      
      console.log('✅ Email simulation completed');
      return true;
    } catch (error) {
      console.error('❌ Failed to send email:', error);
      return false;
    }
  }
}

// Export singleton instance
export const emailService = new EmailService();

// Send PR Team invitation (simple template)
export const sendPrTeamInvitationEmail = async (
  to: string,
  name: string,
  tempPassword: string,
  invitationToken: string,
  senderEmail: string,
  senderName: string
): Promise<boolean> => {
  try {
    const subject = 'Access to FAN EXPO Chicago Press Center';
    const loginUrl = `${window.location.origin}/login`;
    
    const htmlBody = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #ffffff;">
        <h2 style="color: #333; margin-bottom: 20px;">Access to FAN EXPO Chicago Press Center</h2>
        
        <p>Dear <strong>${name}</strong>,</p>
        
        <p style="line-height: 1.6; color: #333; margin-bottom: 20px;">
          You have been granted access to the FAN EXPO Chicago Press Center. Please use the credentials below to log in.
        </p>
        
        <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0;">Login Credentials:</h3>
          <p><strong>Email:</strong> ${to}</p>
          <p><strong>Temporary Password:</strong> <code style="background-color: #e0e0e0; padding: 4px 8px; border-radius: 4px;">${tempPassword}</code></p>
          <p><strong>Login URL:</strong> <a href="${loginUrl}" style="color: #0066cc;">${loginUrl}</a></p>
        </div>
        
        <div style="background-color: #fff3cd; border: 1px solid #ffeaa7; padding: 15px; border-radius: 4px; margin: 20px 0;">
          <p style="margin: 0; color: #856404;"><strong>Important:</strong> You will be prompted to change your password on first login. This temporary password expires in 7 days.</p>
        </div>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${loginUrl}" 
             style="background-color: #0066cc; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block; font-weight: bold;">
            Login Now
          </a>
        </div>
        
        <p style="color: #666; font-size: 14px; margin-top: 30px;">
          If you have any questions, please contact ${senderName} at <a href="mailto:${senderEmail}" style="color: #0066cc;">${senderEmail}</a>.
        </p>
      </div>
    `;

    // Check if we're in production and have a server-side email function
    if (typeof window === 'undefined' || window.location.hostname !== 'localhost') {
      console.log('🚀 PRODUCTION MODE - Calling server-side Gmail function for PR Team invitation');
      
      try {
        // Call Supabase Edge Function to send real email
        const response = await fetch(
          'https://plsafercigygayzlwdqd.supabase.co/functions/v1/send-gmail-email',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              to,
              from: `${senderName} <${senderEmail}>`,
              subject,
              htmlBody,
              senderEmail: senderEmail
            }),
          }
        );

        const result = await response.json();
        
        if (response.ok && result.success) {
          console.log('✅ PR Team email sent successfully via Gmail API:', result.messageId);
          alert(`✅ PR TEAM INVITATION SENT TO: ${name}\n\n📧 EMAIL: ${to}\n🔑 TEMPORARY PASSWORD: ${tempPassword}\n🌐 LOGIN URL: ${loginUrl}\n\n📮 MESSAGE ID: ${result.messageId}`);
          return true;
        } else {
          console.error('❌ Gmail API error:', result.error);
          throw new Error(result.error || 'Gmail API failed');
        }
      } catch (error) {
        console.error('❌ Edge Function call failed:', error);
        // Fall back to development mode
        console.log('📧 Falling back to development mode');
      }
    }

    // Development mode - show email details
    console.log('📧 DEVELOPMENT MODE - PR Team Email simulation:');
    console.log({
      to,
      from: `${senderName} <${senderEmail}>`,
      subject,
      username: to,
      tempPassword,
      loginUrl,
      invitationToken
    });
    
    // Show an alert with the credentials for testing
    alert(`✅ PR TEAM INVITATION SENT TO: ${name}\n\n📧 EMAIL: ${to}\n🔑 TEMPORARY PASSWORD: ${tempPassword}\n🌐 LOGIN URL: ${loginUrl}\n\n⚠️ COPY THIS PASSWORD NOW!\n\n(In production, this would be sent via email)`);
    
    console.log('✅ PR Team email simulation completed');
    return true;
  } catch (error) {
    console.error('❌ Failed to send PR Team email:', error);
    return false;
  }
};

// Export the sendInvitationEmail function for journalist invitations (Fan Expo template)
export const sendInvitationEmail = async (
  to: string,
  name: string,
  tempPassword: string,
  invitationToken: string,
  senderEmail: string,
  senderName: string
): Promise<boolean> => {
  return emailService.sendInvitationEmail(
    to,
    name,
    tempPassword,
    invitationToken,
    senderEmail,
    senderName
  );
};

// Test function for development
export const testGmailConnection = async (userEmail: string): Promise<boolean> => {
  console.log('✅ Email service test - Development mode active');
  console.log('📧 Would test Gmail connection for:', userEmail);
  return true;
};